import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Flex,
  Heading,
} from '@chakra-ui/react';

import { useFormik } from 'formik';
import * as yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import FormInput from '../formComponents/FormInput';
import { validations } from '@/lib/validations';
import { sebiCircularReportDownload } from '@/services/form';
import { errorHandler } from '@/lib/http';

interface IFormModalProps {
  isOpen: boolean;
  //   downloadLink?: any;
  onClose: () => void;
  handleFormComplete?: () => void;
}

interface IFormValues {
  name: string;
  email: string;
  fund_name: string;
  designation: string;
  contact_number: string;
}
const validationSchema = yup.object().shape({
  email: validations.email
    .email('Invalid email address')
    .required('Email is required'),
  name: yup.string().required(' Name is required'),
  fund_name: yup.string().required('Fund name is required'),
  designation: yup.string().required('Designation is required'),
  contact_number: yup
    .string()
    .matches(
      /^\+?([0-9]{2})?\s?\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/,
      'Invalid Number'
    )
    .required('Contact Number is required'),
});

const FormModal: React.FC<IFormModalProps> = ({
  isOpen,
  onClose,
  //   downloadLink,
  handleFormComplete,
}) => {
  // const router = useRouter();

  const { mutate, isLoading } = useMutation(sebiCircularReportDownload, {
    onSuccess: () => {
      //   window.open(downloadLink, '_ blank');
      if (handleFormComplete) {
        handleFormComplete();
      }
      resetForm();
      onClose();
    },
    onError: (err: any) => {
      errorHandler(err);
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    resetForm,
  } = useFormik<IFormValues>({
    initialValues: {
      name: '',
      email: '',
      fund_name: '',
      designation: '',
      contact_number: '',
    },
    validationSchema,
    onSubmit({ name, email, contact_number, designation, fund_name }) {
      mutate({
        data: {
          name,
          email,
          mobile_number: contact_number,
          fund_name,
          designation,
        },
      });
    },
  });

  return (
    <Modal
      onClose={() => {
        onClose();
        resetForm();
      }}
      size="xl"
      isOpen={isOpen}
    >
      <ModalOverlay />
      <ModalContent>
        <Heading variant="milestone" px="20px" pt="14px">
          Please Fill the form to Download!
        </Heading>
        <ModalCloseButton />
        <form onSubmit={handleSubmit}>
          <ModalBody px="36px" pt="30px">
            <Flex columnGap="30px" mb="24px" flexDir="column" rowGap="24px">
              <FormInput
                name="name"
                width="100%"
                label="Name"
                placeholder="Write your name here"
                styleType="recentReports"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.name}
                isRequired
                touched={touched.name}
              />
              <FormInput
                name="email"
                width="100%"
                label="E-mail"
                placeholder="Write your email address"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.email}
                styleType="recentReports"
                isRequired
                touched={touched.email}
              />
              <FormInput
                name="fund_name"
                width="100%"
                label="Fund Name"
                placeholder="What is your fund name"
                value={values.fund_name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.fund_name}
                styleType="recentReports"
                isRequired
                touched={touched.fund_name}
              />
              <FormInput
                name="designation"
                width="100%"
                label="Designation"
                placeholder="What is your designation"
                value={values.designation}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.designation}
                styleType="recentReports"
                isRequired
                touched={touched.designation}
              />
              <FormInput
                name="contact_number"
                width="100%"
                label="Contact No."
                placeholder="Enter your mobile no."
                value={values.contact_number}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.contact_number}
                type="tel"
                styleType="recentReports"
                isRequired
                touched={touched.contact_number}
              />
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button
              borderRadius="0px"
              bgColor="primary.500"
              color="white"
              borderColor="#DADAFB"
              fontSize="14px"
              fontWeight="700"
              lineHeight="21px"
              letterSpacing="0.02em"
              py="13px"
              px="20px"
              h="47px"
              _hover={{ bgColor: 'primary.600' }}
              _active={{ bgColor: 'primary.700' }}
              type="submit"
              isLoading={isLoading}
            >
              Download
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default FormModal;
